import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import KeyActionPanel, { KeyAction } from "@/shared/ui/KeyActionPanel";
import {
  designIsPresentationSelector,
  selectedPreviewDesignVariantSelector,
  setDesignIsPresentation,
} from "@/domains/estate/slice";
import useDesignHandlers from "@/domains/estate/hooks/handlers/useDesignHandlers";
import useShowDesignPresentation from "@/domains/estate/hooks/useShowDesignPresentation";
import EstatateDesignVariantCounter from "@/domains/estate/components/design/EstatateDesignVariantCounter";
import EstateDesignBuyModal from "@/domains/estate/components/design/EstateDesignBuyModal";
import KeyCode from "@/assets/constants/key-code";

export default function EstateDesignVariantPresentationContainer() {
  const dispatch = useAppDispatch();
  const [isBuyDesignModal, setIsBuyDesignModal] = useState(false);
  const isPresentation = useAppSelector(designIsPresentationSelector);
  const selectedDesign = useAppSelector(selectedPreviewDesignVariantSelector);
  const { closePreviewHandler, buyHandler } = useDesignHandlers();
  const { t } = useTranslation("estate");
  const closePresentation = () => {
    closePreviewHandler();
    setIsBuyDesignModal(false);
    dispatch(setDesignIsPresentation(false));
  };

  const closeBuyModal = useCallback(() => {
    setIsBuyDesignModal(false);
  }, []);

  const buyDesign = useCallback(() => {
    buyHandler(selectedDesign.interiorPropId);
    setIsBuyDesignModal(false);
  }, [buyHandler, closeBuyModal]);

  useShowDesignPresentation();

  const actions = new Map<KeyCode, KeyAction>([
    [
      KeyCode.F,
      {
        label: t("estate.buy"),
        keyName: "F",
        action: !isBuyDesignModal ? () => setIsBuyDesignModal(true) : () => {},
        color: "red",
      },
    ],
    [
      KeyCode.ESCAPE,
      {
        label: t("estate.close"),
        keyName: "ESC",
        action: closePresentation,
        color: "red",
      },
    ],
  ]);

  if (!isPresentation) return null;

  return (
    <div className="fixed top-0 left-0 size-full flex justify-end items-end p-7.5">
      {isBuyDesignModal && (
        <EstateDesignBuyModal
          onClose={closeBuyModal}
          onConfirm={buyDesign}
          selectedDesign={selectedDesign}
        />
      )}
      <EstatateDesignVariantCounter
        name={selectedDesign?.name}
        price={selectedDesign?.price}
        isPaused={isBuyDesignModal}
      />
      <KeyActionPanel
        actions={actions}
        name="estate-enter-modal"
        className="fixed bottom-[--boundary-offset-y] left-[50%] -translate-x-[50%] z-[51]"
      />
    </div>
  );
}
